import React, { createContext, useContext, useState, useEffect } from 'react';
import { routes } from '../routes/sidebar';

// Create context
export const FeatureHighlightContext = createContext();

// Feature configuration - add properties for each feature we might want to highlight
const featureConfig = {
  'Detect AI': {
    importance: 10,
    description: 'Check if content was written by AI',
    showProbability: 0.05, // Higher number = more likely to be shown
  },
  'Humanize AI': {
    importance: 9,
    description: 'Make AI-generated text sound more human',
    showProbability: 0.05,
  },
  'All AI Chat': {
    importance: 8, 
    description: 'Chat with OpenAI, Claude, and more',
    showProbability: 0.12,
  },
  'Chat with PDF': {
    importance: 7,
    description: 'Upload documents and chat with them',
    showProbability: 0.12,
  },
  'Paraphraser': {
    importance: 5,
    description: 'Rewrite content with different wording',
    showProbability: 0.15,
  },
  'Grammar Checker': {
    importance: 4,
    description: 'Fix grammar and spelling errors',
    showProbability: 0.1,
  },
  'Plagiarism Check': {
    importance: 5,
    description: 'Check content for plagiarism',
    showProbability: 0.1,
  },
  'Earn Free Credits': {
    importance: 3,
    description: 'Complete tasks to earn free credits',
    showProbability: 0.05,
  },
};

// Provider component
export const FeatureHighlightProvider = ({ children }) => {
  const [highlightedFeature, setHighlightedFeature] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [featureUsage, setFeatureUsage] = useState({});

  // Load feature usage data from localStorage on component mount
  useEffect(() => {
    const storedUsage = localStorage.getItem('featureUsage');
    if (storedUsage) {
      setFeatureUsage(JSON.parse(storedUsage));
    }
    
    // FOR TESTING: Always show a highlight after a short delay
    // setTimeout(() => {
    //   selectFeatureToHighlight();
    // }, 1500); // Wait 1.5 seconds after load
    
    // NOTE: For production, uncomment the code below and remove the testing code above
    // Check if we should show a highlight in this session
    const lastHighlightTime = localStorage.getItem('lastHighlightTime');
    const currentTime = new Date().getTime();
    
    // Only show highlights if it's been at least 1 hour since the last one
    // or if there's no record of previous highlights
    const shouldShowHighlight = !lastHighlightTime || 
      (currentTime - parseInt(lastHighlightTime, 10) > 1 * 60 * 60 * 1000);
      
    if (shouldShowHighlight) {
      // Wait a moment before showing the highlight
      setTimeout(() => {
        selectFeatureToHighlight();
      }, 3000); // Wait 3 seconds after load
      
      // Record this highlight time
      localStorage.setItem('lastHighlightTime', currentTime);
    }
  }, []);

  // Save feature usage data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('featureUsage', JSON.stringify(featureUsage));
  }, [featureUsage]);

  // Select a feature to highlight based on usage data and configuration
  const selectFeatureToHighlight = () => {
    // Get all sidebar features that have configs
    const allFeatures = routes.userRoutes
      .filter(route => route.name && featureConfig[route.name])
      .map(route => ({
        name: route.name,
        path: route.path,
        icon: route.icon,
        ...featureConfig[route.name]
      }));

    // Calculate scores for each feature (lower usage = higher score)
    const scoredFeatures = allFeatures.map(feature => {
      const usageCount = featureUsage[feature.name] || 0;
      // Base score is importance + random factor, divided by (usage count + 1)
      const score = (feature.importance + (Math.random() * 5)) / (usageCount + 1) * feature.showProbability;
      return { ...feature, score };
    });

    // Sort by score (highest first) and take the top one
    scoredFeatures.sort((a, b) => b.score - a.score);
    const selectedFeature = scoredFeatures[0];
    
    // FOR TESTING: Always show the feature highlight if we have a feature to show
    // if (selectedFeature) {
    //   console.log('Highlighting feature:', selectedFeature.name);
    //   setHighlightedFeature(selectedFeature);
    //   setIsVisible(true);
    // }
    
    // PRODUCTION CODE (uncomment later)
    if (selectedFeature && Math.random() < 0.7) { // 70% chance to actually show a highlight
      setHighlightedFeature(selectedFeature);
      setIsVisible(true);
    }
  };

  // Record that a user has used a feature
  const recordFeatureUsage = (featureName) => {
    setFeatureUsage(prev => ({
      ...prev,
      [featureName]: (prev[featureName] || 0) + 1
    }));
  };

  // Hide the highlight
  const hideHighlight = (e) => {
    // If event is provided, prevent default and stop propagation
    if (e && typeof e.stopPropagation === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsVisible(false);
  };

  // Value to be provided by the context
  const value = {
    highlightedFeature,
    isVisible,
    hideHighlight,
    recordFeatureUsage
  };

  return (
    <FeatureHighlightContext.Provider value={value}>
      {children}
    </FeatureHighlightContext.Provider>
  );
};

// Custom hook for using the context
export const useFeatureHighlight = () => useContext(FeatureHighlightContext);